import React from 'react';
import { Box, Button } from '@mui/material';
import { Stack } from '@mui/system';
import { useMutation } from '@tanstack/react-query';
import useBundleTranslation from 'i18n';
import { FormControlProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import { FormComponentBuilder } from 'components/common/form/layout';
import { useOpenTestSession } from 'app/concierge/hooks/useOpenTestSession';
import IconMi from 'components/common/icon/IconMi';

export interface ConciergeDomainPreviewProps extends FormControlProps {
    entityId: number;
    // Info-tab routing toggle; the helper line under the button follows it live
    enabledField: string;
    /** The preview is always on since the configuration cleanup (cut 2a); the server passes true. */
    previewEnabled?: boolean;
}

// Ask THIS domain a question from its own editor page, whatever its knowledge-graph status and
// whether or not it is switched on (Marius, 2026-09-04). Sits above the Knowledge Graph section
// (Kristi, 2026-09-14), so it is its own form component rather than part of ConciergeKgControl.
export default function ConciergeDomainPreview({
    controlProps,
}: FormElementControlPropsType<ConciergeDomainPreviewProps>) {
    const { t } = useBundleTranslation(['components/common/concierge-kg-control-domain']);
    const enabledField = controlProps.enabledField;
    const [enabledInd, setEnabledInd] = React.useState<string | undefined>(
        () => controlProps.form.hookFormGetValues(enabledField) as string,
    );
    React.useEffect(() => {
        setEnabledInd(controlProps.form.hookFormGetValues(enabledField) as string);
        const subscription = controlProps.form.hookFormWatch((values, { name }) => {
            if (!name || name === enabledField) {
                setEnabledInd(values[enabledField] as string);
            }
        });
        return () => subscription.unsubscribe();
    }, [enabledField]);

    const openTestSession = useOpenTestSession();
    const preview = useMutation({
        mutationFn: () => openTestSession({ conciergeDomainId: controlProps.entityId }),
    });

    if (controlProps.previewEnabled !== true) {
        return null;
    }

    return (
        <Stack spacing={1} data-test="concierge_domain_preview">
            <Box>
                <Button
                    variant="outlined"
                    startIcon={<IconMi icon="concierge" sx={{ width: 16, height: 16 }} />}
                    data-test="concierge_kg_btn_test_domain"
                    onClick={() => preview.mutate()}
                    disabled={preview.isPending}
                >
                    {t('btn_test_in_concierge')}
                </Button>
            </Box>
            <Box
                sx={{
                    color: 'text.secondary',
                    fontSize: (theme) => theme.size.pxValue(theme.typography.fontSize - 1),
                }}
                data-test="concierge_domain_preview_helper"
            >
                {t(enabledInd === 'N' ? 'routing_line_disabled' : 'routing_line_enabled')}
            </Box>
        </Stack>
    );
}

export class ConciergeDomainPreviewBuilder extends FormComponentBuilder {
    prepareProps(): ConciergeDomainPreviewProps {
        return {
            ...this.controlProps,
            entityId: this.elementProps.componentProps?.entityId,
            enabledField: this.elementProps.componentProps?.enabledField,
            previewEnabled: this.elementProps.componentProps?.previewEnabled,
        };
    }
}
